import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { t } from '../i18n/index.ts';
import { browserTimeZone, loadSettings, saveSettings } from '../lib/settings.ts';
import { SectionHeader } from './ui.tsx';

const FALLBACK = 'Europe/London';

function allZones(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [];
  }
}

/** Which day "today" is for the map, Claude and shared links: the stored zone, else the browser's. */
export function TimeZoneSetting() {
  const browser = browserTimeZone() ?? FALLBACK;
  const [value, setValue] = useState(browser);
  const [failed, setFailed] = useState(false);
  const saved = useRef(browser);
  const latest = useRef(0);
  const touched = useRef(false);
  const id = useId();
  const zones = useMemo(() => {
    const list = allZones();
    return list.includes(value) ? list : [value, ...list].sort();
  }, [value]);

  useEffect(() => {
    let live = true;
    loadSettings().then(
      (settings) => {
        if (!live || touched.current || !settings.timeZone) return;
        saved.current = settings.timeZone;
        setValue(settings.timeZone);
      },
      () => {},
    );
    return () => { live = false; };
  }, []);

  const choose = async (next: string) => {
    touched.current = true;
    const request = ++latest.current;
    setValue(next);
    setFailed(false);
    try {
      const settings = await saveSettings({ timeZone: next });
      saved.current = settings.timeZone ?? next;
    } catch {
      if (request !== latest.current) return;
      setValue(saved.current);
      setFailed(true);
    }
  };

  return (
    <section className="card card--pad-lg stack-md" aria-labelledby={id}>
      <SectionHeader title={t('settings.timeZone.title')} id={id} />
      <p className="muted-2" id={`${id}-hint`}>{t('settings.timeZone.hint')}</p>
      <select
        className="input"
        aria-labelledby={id}
        aria-describedby={`${id}-hint`}
        value={value}
        onChange={(e) => void choose(e.target.value)}
      >
        {zones.map((zone) => <option key={zone} value={zone}>{zone.replace(/_/g, ' ')}</option>)}
      </select>
      <p className="choice-status" role="status" aria-live="polite">{failed ? t('settings.timeZone.error') : ''}</p>
    </section>
  );
}
